/**
 * Shopify CRO (Conversion Rate Optimization)
 * Dashboard for Shopify store optimization, product analysis, and A/B testing
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Store,
    RefreshCw,
    Plus,
    TrendingUp,
    Package,
    Layout,
    ShoppingCart,
    TestTube,
    AlertCircle,
    Loader2,
    DollarSign,
    Percent,
    ShoppingBag,
    ArrowUpRight,
    ArrowDownRight,
    ExternalLink
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import ShopifyConnectModal from '../components/shopify-cro/ShopifyConnectModal';
import * as shopifyCROService from '../services/shopifyCROService';

const ShopifyCRO = () => {
    const { currentUser } = useAuth();
    const { selectedBrand } = useApp();

    // Connection state
    const [connections, setConnections] = useState([]);
    const [selectedConnection, setSelectedConnection] = useState(null);
    const [showConnectModal, setShowConnectModal] = useState(false);

    // Data state
    const [storeInfo, setStoreInfo] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [products, setProducts] = useState([]);
    const [collections, setCollections] = useState([]);

    // UI state
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview'); // overview, products, collections, tests

    // Load connections on mount
    useEffect(() => {
        if (currentUser && selectedBrand) {
            loadConnections();
        }
    }, [currentUser, selectedBrand]);

    // Load store data when connection is selected
    useEffect(() => {
        if (selectedConnection) {
            loadStoreData();
        }
    }, [selectedConnection]);

    const loadConnections = async () => {
        setLoading(true);
        setError(null);
        try {
            const conns = await shopifyCROService.getShopifyConnections(
                currentUser.uid,
                selectedBrand.id
            );
            setConnections(conns);
            if (conns.length > 0) {
                setSelectedConnection(conns[0]);
            }
        } catch (err) {
            console.error('Error loading connections:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadStoreData = async () => {
        if (!selectedConnection) return;

        setRefreshing(true);
        setError(null);
        try {
            const [info, analyticsData, productData, collectionData] = await Promise.all([
                shopifyCROService.getStoreInfo(selectedConnection.id, currentUser.uid),
                shopifyCROService.getAnalytics(selectedConnection.id, currentUser.uid),
                shopifyCROService.getProducts(selectedConnection.id, currentUser.uid, 20),
                shopifyCROService.getCollections(selectedConnection.id, currentUser.uid)
            ]);

            setStoreInfo(info);
            setAnalytics(analyticsData);
            setProducts(productData);
            setCollections(collectionData);
        } catch (err) {
            console.error('Error loading store data:', err);
            setError(err.message);
        } finally {
            setRefreshing(false);
        }
    };

    const handleConnectionSuccess = async (connectionId) => {
        await loadConnections();
    };

    const handleDisconnect = async () => {
        if (!selectedConnection) return;
        try {
            await shopifyCROService.disconnectShopify(selectedConnection.id, currentUser.uid);
            setSelectedConnection(null);
            setStoreInfo(null);
            setAnalytics(null);
            setProducts([]);
            setCollections([]);
            await loadConnections();
        } catch (err) {
            console.error('Error disconnecting:', err);
            setError(err.message);
        }
    };

    const formatCurrency = (value, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(value || 0);
    };

    const formatPercent = (value) => {
        return `${parseFloat(value || 0).toFixed(1)}%`;
    };

    // No brand selected
    if (!selectedBrand) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                gap: 'var(--space-4)'
            }}>
                <Store size={48} style={{ color: 'var(--color-text-tertiary)' }} />
                <h2 style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                    Select a brand to view Store CRO
                </h2>
                <p style={{ color: 'var(--color-text-tertiary)', margin: 0 }}>
                    Choose a brand from the sidebar to connect and optimize your Shopify store
                </p>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                gap: 'var(--space-4)'
            }}>
                <Loader2 size={32} className="spinning" style={{ color: 'var(--color-primary)' }} />
                <p style={{ color: 'var(--color-text-secondary)' }}>Loading Shopify connections...</p>
            </div>
        );
    }

    // No connections - show connect CTA
    if (connections.length === 0) {
        return (
            <div style={{ padding: 'var(--space-6)' }}>
                <div className="card" style={{
                    maxWidth: '600px',
                    margin: '0 auto',
                    textAlign: 'center',
                    padding: 'var(--space-8)'
                }}>
                    <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: 'var(--radius-xl)',
                        background: 'linear-gradient(135deg, #96bf48 0%, #5c8a2f 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto var(--space-6)'
                    }}>
                        <Store size={40} color="white" />
                    </div>

                    <h2 style={{ margin: 0, marginBottom: 'var(--space-2)' }}>
                        Connect Your Shopify Store
                    </h2>
                    <p className="text-secondary" style={{ margin: 0, marginBottom: 'var(--space-6)' }}>
                        Get AI-powered CRO recommendations, optimize product pages, and run A/B tests to increase conversions
                    </p>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 'var(--space-4)',
                        marginBottom: 'var(--space-6)',
                        textAlign: 'left'
                    }}>
                        <div style={{ padding: 'var(--space-4)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                            <Package size={24} style={{ color: '#96bf48', marginBottom: 'var(--space-2)' }} />
                            <h4 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>Product Optimization</h4>
                            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                Improve titles, descriptions, and images
                            </p>
                        </div>
                        <div style={{ padding: 'var(--space-4)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                            <ShoppingCart size={24} style={{ color: '#96bf48', marginBottom: 'var(--space-2)' }} />
                            <h4 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>Cart Recovery</h4>
                            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                Reduce abandonment rates
                            </p>
                        </div>
                        <div style={{ padding: 'var(--space-4)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                            <TestTube size={24} style={{ color: '#96bf48', marginBottom: 'var(--space-2)' }} />
                            <h4 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>A/B Testing</h4>
                            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                Test variations to find winners
                            </p>
                        </div>
                    </div>

                    <button
                        className="btn btn-primary"
                        onClick={() => setShowConnectModal(true)}
                        style={{
                            background: 'linear-gradient(135deg, #96bf48 0%, #5c8a2f 100%)',
                            padding: 'var(--space-3) var(--space-6)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                        }}
                    >
                        <Plus size={18} />
                        Connect Shopify Store
                    </button>
                </div>

                <ShopifyConnectModal
                    isOpen={showConnectModal}
                    onClose={() => setShowConnectModal(false)}
                    brand={selectedBrand}
                    onSuccess={handleConnectionSuccess}
                />
            </div>
        );
    }

    // Main CRO Dashboard
    return (
        <div style={{ padding: 'var(--space-6)' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-6)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: 'var(--radius-lg)',
                        background: 'linear-gradient(135deg, #96bf48 0%, #5c8a2f 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Store size={24} color="white" />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>Store CRO</h1>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                                {selectedConnection?.shopName || selectedConnection?.shopDomain}
                            </span>
                            {selectedConnection?.shopDomain && (
                                <a
                                    href={`https://${selectedConnection.shopDomain}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'var(--color-primary)', display: 'flex', alignItems: 'center' }}
                                >
                                    <ExternalLink size={14} />
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    <button
                        className="btn btn-secondary"
                        onClick={loadStoreData}
                        disabled={refreshing}
                        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                    >
                        <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
                        Sync
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowConnectModal(true)}
                        style={{
                            background: 'linear-gradient(135deg, #96bf48 0%, #5c8a2f 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                        }}
                    >
                        <Plus size={16} />
                        Add Store
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div
                    className="card"
                    style={{
                        background: 'var(--color-error-bg)',
                        border: '1px solid var(--color-error)',
                        marginBottom: 'var(--space-6)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)'
                    }}
                >
                    <AlertCircle size={20} style={{ color: 'var(--color-error)' }} />
                    <span style={{ color: 'var(--color-error)' }}>{error}</span>
                </div>
            )}

            {/* Analytics Overview Cards */}
            {analytics && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 'var(--space-4)',
                    marginBottom: 'var(--space-6)'
                }}>
                    <div className="card" style={{ padding: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                    Revenue (30d)
                                </p>
                                <h2 style={{ margin: 0 }}>
                                    {formatCurrency(analytics.totalRevenue, storeInfo?.shop?.currency)}
                                </h2>
                            </div>
                            <div style={{
                                padding: 'var(--space-2)',
                                background: 'var(--color-success-bg)',
                                borderRadius: 'var(--radius-md)'
                            }}>
                                <DollarSign size={20} style={{ color: 'var(--color-success)' }} />
                            </div>
                        </div>
                    </div>

                    <div className="card" style={{ padding: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                    Orders (30d)
                                </p>
                                <h2 style={{ margin: 0 }}>
                                    {analytics.totalOrders || 0}
                                </h2>
                            </div>
                            <div style={{
                                padding: 'var(--space-2)',
                                background: 'var(--color-primary-bg)',
                                borderRadius: 'var(--radius-md)'
                            }}>
                                <ShoppingBag size={20} style={{ color: 'var(--color-primary)' }} />
                            </div>
                        </div>
                    </div>

                    <div className="card" style={{ padding: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                    Avg Order Value
                                </p>
                                <h2 style={{ margin: 0 }}>
                                    {formatCurrency(analytics.averageOrderValue, storeInfo?.shop?.currency)}
                                </h2>
                            </div>
                            <div style={{
                                padding: 'var(--space-2)',
                                background: 'var(--color-warning-bg)',
                                borderRadius: 'var(--radius-md)'
                            }}>
                                <TrendingUp size={20} style={{ color: 'var(--color-warning)' }} />
                            </div>
                        </div>
                    </div>

                    <div className="card" style={{ padding: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                    Cart Abandonment
                                </p>
                                <h2 style={{ margin: 0, color: parseFloat(analytics.cartAbandonmentRate) > 50 ? 'var(--color-error)' : 'inherit' }}>
                                    {formatPercent(analytics.cartAbandonmentRate)}
                                </h2>
                            </div>
                            <div style={{
                                padding: 'var(--space-2)',
                                background: parseFloat(analytics.cartAbandonmentRate) > 50 ? 'var(--color-error-bg)' : 'var(--color-bg-tertiary)',
                                borderRadius: 'var(--radius-md)'
                            }}>
                                <ShoppingCart size={20} style={{
                                    color: parseFloat(analytics.cartAbandonmentRate) > 50 ? 'var(--color-error)' : 'var(--color-text-secondary)'
                                }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab Navigation */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                borderBottom: '1px solid var(--color-border)',
                marginBottom: 'var(--space-6)'
            }}>
                {[
                    { id: 'overview', label: 'Overview', icon: Layout },
                    { id: 'products', label: 'Products', icon: Package },
                    { id: 'collections', label: 'Collections', icon: Store },
                    { id: 'tests', label: 'A/B Tests', icon: TestTube }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-3) var(--space-4)',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: activeTab === tab.id ? '2px solid #96bf48' : '2px solid transparent',
                            color: activeTab === tab.id ? '#96bf48' : 'var(--color-text-secondary)',
                            fontWeight: activeTab === tab.id ? 600 : 400,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        <tab.icon size={16} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div>
                    <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                        <h3 style={{ margin: 0, marginBottom: 'var(--space-4)' }}>Store Overview</h3>
                        {storeInfo ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 'var(--space-4)'
                            }}>
                                <div>
                                    <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Store Name</p>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{storeInfo.shop?.name}</p>
                                </div>
                                <div>
                                    <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Products</p>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{storeInfo.productCount || 0}</p>
                                </div>
                                <div>
                                    <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Total Orders</p>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{storeInfo.orderCount || 0}</p>
                                </div>
                                <div>
                                    <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Plan</p>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{storeInfo.shop?.plan || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Currency</p>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{storeInfo.shop?.currency || 'USD'}</p>
                                </div>
                                <div>
                                    <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Theme</p>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{storeInfo.activeTheme?.name || 'N/A'}</p>
                                </div>
                            </div>
                        ) : (
                            <p className="text-secondary">Loading store info...</p>
                        )}
                    </div>

                    <div className="card">
                        <h3 style={{ margin: 0, marginBottom: 'var(--space-4)' }}>CRO Recommendations</h3>
                        <p className="text-secondary" style={{ margin: 0 }}>
                            AI-powered CRO analysis coming soon. Connect your store to get personalized recommendations.
                        </p>
                    </div>
                </div>
            )}

            {activeTab === 'products' && (
                <div className="card">
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <h3 style={{ margin: 0 }}>Products</h3>
                        <span className="text-secondary">{products.length} products</span>
                    </div>

                    {products.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                            {products.map(product => (
                                <div
                                    key={product.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-4)',
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-md)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {product.images?.[0]?.src ? (
                                        <img
                                            src={product.images[0].src}
                                            alt={product.title}
                                            style={{
                                                width: '48px',
                                                height: '48px',
                                                objectFit: 'cover',
                                                borderRadius: 'var(--radius-sm)'
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '48px',
                                            height: '48px',
                                            background: 'var(--color-bg-tertiary)',
                                            borderRadius: 'var(--radius-sm)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Package size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                                        </div>
                                    )}
                                    <div style={{ flex: 1 }}>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{product.title}</p>
                                        <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                            {product.variants?.length || 0} variants
                                        </p>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <p style={{ margin: 0, fontWeight: 500 }}>
                                            {formatCurrency(product.variants?.[0]?.price, storeInfo?.shop?.currency)}
                                        </p>
                                        <span style={{
                                            fontSize: 'var(--font-size-xs)',
                                            padding: 'var(--space-1) var(--space-2)',
                                            background: product.status === 'active' ? 'var(--color-success-bg)' : 'var(--color-bg-tertiary)',
                                            color: product.status === 'active' ? 'var(--color-success)' : 'var(--color-text-secondary)',
                                            borderRadius: 'var(--radius-sm)'
                                        }}>
                                            {product.status}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-secondary">No products found.</p>
                    )}
                </div>
            )}

            {activeTab === 'collections' && (
                <div className="card">
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <h3 style={{ margin: 0 }}>Collections</h3>
                        <span className="text-secondary">{collections.length} collections</span>
                    </div>

                    {collections.length > 0 ? (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: 'var(--space-4)'
                        }}>
                            {collections.map(collection => (
                                <div
                                    key={collection.id}
                                    style={{
                                        padding: 'var(--space-4)',
                                        background: 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-md)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                        <Store size={16} style={{ color: '#96bf48' }} />
                                        <span style={{
                                            fontSize: 'var(--font-size-xs)',
                                            padding: 'var(--space-1) var(--space-2)',
                                            background: 'var(--color-bg-tertiary)',
                                            borderRadius: 'var(--radius-sm)',
                                            textTransform: 'capitalize'
                                        }}>
                                            {collection.type}
                                        </span>
                                    </div>
                                    <h4 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>{collection.title}</h4>
                                    {collection.body_html && (
                                        <p className="text-secondary" style={{
                                            margin: 0,
                                            fontSize: 'var(--font-size-sm)',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}>
                                            {collection.body_html.replace(/<[^>]*>/g, '').substring(0, 100)}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-secondary">No collections found.</p>
                    )}
                </div>
            )}

            {activeTab === 'tests' && (
                <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                    <TestTube size={48} style={{ color: 'var(--color-text-tertiary)', margin: '0 auto var(--space-4)' }} />
                    <h3 style={{ margin: 0, marginBottom: 'var(--space-2)' }}>A/B Testing</h3>
                    <p className="text-secondary" style={{ margin: 0, marginBottom: 'var(--space-6)' }}>
                        Create split tests to optimize your product pages, CTAs, and checkout flow.
                    </p>
                    <button
                        className="btn btn-primary"
                        style={{ background: 'linear-gradient(135deg, #96bf48 0%, #5c8a2f 100%)' }}
                        disabled
                    >
                        <Plus size={16} style={{ marginRight: 'var(--space-2)' }} />
                        Create A/B Test (Coming Soon)
                    </button>
                </div>
            )}

            {/* Connect Modal */}
            <ShopifyConnectModal
                isOpen={showConnectModal}
                onClose={() => setShowConnectModal(false)}
                brand={selectedBrand}
                onSuccess={handleConnectionSuccess}
            />
        </div>
    );
};

export default ShopifyCRO;
